import { clockWords, shownTime, weekdayDayMonth } from './round-config'
import { setTimesOn } from './business-settings'
// "Your jobs changed since Fri 17:40" (docs/plans/offline/PLAN-v2.md §5, release 2 of "Roundly opens with no
// signal", 1 Oct 2026). A worker who worked from the copy on his phone needs telling, once he has signal again,
// what the owner changed meanwhile: a job added, one taken off his list, one moved to another day or time, an
// address changed. Pure, so it is tested on its own (scripts/offline-r2r3.test.mjs); Today (screens/today.ts)
// keeps the records and draws the card.
//
// The baseline is the five days kept on the phone as he last had them, one small record per visit (date, time,
// customer, address, state), kept apart from the saved days themselves so no fresh read can overwrite it before
// it has been compared. Each time the five days are downloaded again they are compared with it, visit by visit:
// any difference becomes the card, which stays (across restarts) until he taps OK; only then does the baseline
// move on to what he has now been shown. Nothing to tell, and the baseline simply moves on.
//
// His own changes are left out field by field, never the whole visit: a difference is skipped only when every
// field it is made of is one his own waiting changes set (ownFields). A Done waiting to send on a job the owner has
// since moved still shows the move (Astra, 1 Oct). What he can change himself (done, paid, notes, couldn't do it,
// times) is never one of the four kinds of change the card tells him about anyway.

export interface SeenRow {
  id: number
  date: string
  time: string | null
  name: string
  address: string | null
  state: string
  // Several jobs (step 2): the job's own name when the download carried one. Absent on rows saved before step 2,
  // which are read as they are: no rename is ever found against them.
  job?: string | null
}

export interface SeenWindow {
  v: 1
  from: string
  to: string
  at: number // when the phone downloaded it
  rows: SeenRow[]
}

export type ChangeKind = 'added' | 'moved' | 'removed' | 'address' | 'renamed'

export interface JobChange {
  kind: ChangeKind
  id: number
  name: string
  date: string
  time: string | null
  address: string | null
  // Several jobs: which job, by its name, when the visit has one.
  job?: string | null
  // How it was, for a move, a removal, a new address or a renamed job.
  was?: { date: string; time: string | null; address: string | null; job?: string | null }
}

export interface ChangedCard {
  v: 1
  since: number // the baseline's download time: "since Fri 17:40"
  changes: JobChange[]
}

interface WindowLike {
  from: string
  to: string
  cleans: readonly { id: number; date: string; time_slot: string | null; customer_name: string; address: string | null; postcode?: string | null; state: string; job_name?: string | null }[]
}

// The address as the card shows it: the address line, with the postcode when it is not already in it.
function addressOf(c: { address: string | null; postcode?: string | null }): string | null {
  const a = (c.address ?? '').trim()
  const p = (c.postcode ?? '').trim()
  if (!a) return p || null
  return p && !a.toUpperCase().includes(p.toUpperCase()) ? `${a}, ${p}` : a
}

export function seenOf(data: WindowLike, at: number): SeenWindow {
  return {
    v: 1,
    from: data.from,
    to: data.to,
    at,
    rows: data.cleans.map((c) => ({ id: c.id, date: c.date, time: c.time_slot ?? null, name: c.customer_name, address: addressOf(c), state: c.state, ...('job_name' in c ? { job: c.job_name?.trim() || null } : {}) })),
  }
}

export function isSeenWindow(x: unknown): x is SeenWindow {
  const w = x as SeenWindow | null
  return !!w && w.v === 1 && typeof w.from === 'string' && typeof w.to === 'string' && typeof w.at === 'number' && Array.isArray(w.rows)
}

export function isChangedCard(x: unknown): x is ChangedCard {
  const c = x as ChangedCard | null
  return !!c && c.v === 1 && typeof c.since === 'number' && Array.isArray(c.changes) && c.changes.length > 0
}

interface QueuedLike {
  method: string
  path: string
  body?: unknown
  status: string
}
const CLEAN_PATH = /^\/api\/cleans\/(\d+)$/

// The fields of each visit his changes still waiting to send set ({ 12: {state, payment_status} }). Refused ones
// (conflict, failed) are not his copy of the visit any more: the owner's version stands, so they count for nothing.
export function ownFields(queue: readonly QueuedLike[]): Map<number, Set<string>> {
  const out = new Map<number, Set<string>>()
  for (const q of queue) {
    const m = q.method === 'PATCH' ? CLEAN_PATH.exec(q.path) : null
    if (!m || q.status === 'conflict' || q.status === 'failed' || !q.body || typeof q.body !== 'object') continue
    const id = Number(m[1])
    const set = out.get(id) ?? new Set<string>()
    for (const k of Object.keys(q.body as Record<string, unknown>)) set.add(k)
    out.set(id, set)
  }
  return out
}

// The job on a change line, only when the visit's row carries a name for it.
function jobOf(r: SeenRow): { job?: string } {
  return r.job ? { job: r.job } : {}
}

const ORDER: Record<ChangeKind, number> = { added: 0, moved: 1, removed: 2, address: 3, renamed: 4 }

// What changed between the baseline and a fresh download, visit by visit. Only days both copies hold count: the
// window moves on a day at a time, and a day dropping off the back or a new one arriving at the front is not a
// change. A visit that is in both copies is compared wherever it now is (a job moved from yesterday onto tomorrow
// is a move). `own`: the fields his changes still waiting to send set, per visit (ownFields): a difference made
// only of those is his, and left out. A job added or taken off his list is never his doing (he can do neither).
export function diffWindows(before: SeenWindow, after: SeenWindow, own: ReadonlyMap<number, ReadonlySet<string>> = new Map()): JobChange[] {
  const from = before.from > after.from ? before.from : after.from
  const to = before.to < after.to ? before.to : after.to
  const inBoth = (d: string): boolean => d >= from && d <= to
  const now = new Map(after.rows.map((r) => [r.id, r]))
  const then = new Map(before.rows.map((r) => [r.id, r]))
  const out: JobChange[] = []
  for (const b of before.rows) {
    const a = now.get(b.id)
    if (!a) {
      if (inBoth(b.date)) out.push({ kind: 'removed', id: b.id, name: b.name, date: b.date, time: b.time, address: b.address, ...jobOf(b), was: { date: b.date, time: b.time, address: b.address } })
      continue
    }
    const mine = own.get(b.id)
    const his = (fields: string[]): boolean => !!mine && fields.length > 0 && fields.every((f) => mine.has(f))
    const was = { date: b.date, time: b.time, address: b.address }
    const moved: string[] = []
    if (a.date !== b.date) moved.push('date')
    // No set times (Your business): a time is hidden, so it never makes a visit read as moved.
    if (setTimesOn() && (a.time ?? null) !== (b.time ?? null)) moved.push('time_slot')
    if (moved.length && !his(moved)) out.push({ kind: 'moved', id: a.id, name: a.name, date: a.date, time: a.time, address: a.address, ...jobOf(a), was })
    else if ((a.address ?? null) !== (b.address ?? null) && !his(['address'])) out.push({ kind: 'address', id: a.id, name: a.name, date: a.date, time: a.time, address: a.address, ...jobOf(a), was })
    // A job renamed by the owner (several jobs): only when both copies carry the job's name, so a copy saved
    // before step 2 never reads as every job renamed.
    else if ('job' in b && 'job' in a && (a.job ?? null) !== (b.job ?? null)) out.push({ kind: 'renamed', id: a.id, name: a.name, date: a.date, time: a.time, address: a.address, ...jobOf(a), was: { ...was, job: b.job ?? null } })
  }
  for (const a of after.rows) {
    if (then.has(a.id) || !inBoth(a.date)) continue
    out.push({ kind: 'added', id: a.id, name: a.name, date: a.date, time: a.time, address: a.address, ...jobOf(a) })
  }
  return out.sort((x, y) => ORDER[x.kind] - ORDER[y.kind] || x.date.localeCompare(y.date) || (x.time ?? '').localeCompare(y.time ?? '') || x.id - y.id)
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const two = (n: number): string => String(n).padStart(2, '0')

// "Fri 17:40", on the phone's clock; a week or more ago also says the date ("Fri 25 Sep 17:40").
export function sinceLabel(ts: number, now = Date.now()): string {
  const d = new Date(ts)
  const clock = clockWords(d.getHours(), two(d.getMinutes()))
  const old = now - ts >= 6 * 86_400_000
  return old ? `${weekdayDayMonth(WEEKDAYS[d.getDay()]!, d.getDate(), MONTHS[d.getMonth()]!)} ${clock}` : `${WEEKDAYS[d.getDay()]} ${clock}`
}

// "Mon 5 Oct" or "Mon 5 Oct 10:00".
function whenOf(date: string, time: string | null): string {
  const d = new Date(`${date}T12:00:00`)
  const day = weekdayDayMonth(WEEKDAYS[d.getDay()]!, d.getDate(), MONTHS[d.getMonth()]!)
  return time ? `${day} ${shownTime(time.slice(0, 5))}` : day
}

// "Your jobs changed since Fri 17:40: 1 added, 1 moved, 1 removed, 1 new address"
export function cardTitle(card: ChangedCard, now = Date.now()): string {
  const n = (k: ChangeKind): number => card.changes.filter((c) => c.kind === k).length
  const parts: string[] = []
  if (n('added')) parts.push(`${n('added')} added`)
  if (n('moved')) parts.push(`${n('moved')} moved`)
  if (n('removed')) parts.push(`${n('removed')} removed`)
  if (n('address')) parts.push(n('address') === 1 ? '1 new address' : `${n('address')} new addresses`)
  if (n('renamed')) parts.push(`${n('renamed')} renamed`)
  return `Your jobs changed since ${sinceLabel(card.since, now)}: ${parts.join(', ')}`
}

// One line per change, in the worker's words.
// With several jobs the job is named after the customer: "Moved: Mrs Smith (Gutters), now ...".
export function changeLine(change: JobChange): string {
  // No set times (Your business): the card names the day only.
  const c: JobChange = setTimesOn() ? change : { ...change, time: null, ...(change.was ? { was: { ...change.was, time: null } } : {}) }
  const who = c.job ? `${c.name} (${c.job})` : c.name
  if (c.kind === 'added') return `Added: ${who}, ${whenOf(c.date, c.time)}${c.address ? `, ${c.address}` : ''}`
  if (c.kind === 'removed') return `Removed: ${who}, was ${whenOf(c.date, c.time)}${c.address ? `, ${c.address}` : ''}`
  if (c.kind === 'moved') return `Moved: ${who}, now ${whenOf(c.date, c.time)} (was ${whenOf(c.was?.date ?? c.date, c.was?.time ?? null)})`
  if (c.kind === 'renamed') return `Job renamed: ${c.name}, ${whenOf(c.date, c.time)}, now ${c.job ?? 'no name'}${c.was?.job ? ` (was ${c.was.job})` : ''}`
  return `New address: ${who}, ${whenOf(c.date, c.time)}, now ${c.address ?? 'no address'}${c.was?.address ? ` (was ${c.was.address})` : ''}`
}
